import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Switch, Select, Space, Tag, Typography, Row, Col, message } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDocumentSequences, useSaveDocumentSequence } from '@/hooks/useSettings';
import type { DocumentSequence } from '@/services/settingsService';
import settingsService from '@/services/settingsService';
import { useAuthStore } from '@/store/authStore';

const { Text } = Typography;
const { Option } = Select;
const { Search } = Input;

type ResetPolicy = 'never' | 'yearly' | 'monthly';

function resetPolicyFromRecord(record: DocumentSequence): ResetPolicy {
  if (record.reset_monthly) return 'monthly';
  if (record.reset_yearly) return 'yearly';
  return 'never';
}

function resetFlagsFromPolicy(policy: ResetPolicy): { reset_yearly: boolean; reset_monthly: boolean } {
  if (policy === 'monthly') return { reset_yearly: false, reset_monthly: true };
  if (policy === 'yearly') return { reset_yearly: true, reset_monthly: false };
  return { reset_yearly: false, reset_monthly: false };
}

function formatTags(record: DocumentSequence): React.ReactNode {
  const pad = Math.max(1, Number(record.padding_length) || 4);
  return (
    <Space wrap size={[4, 4]}>
      {record.prefix ? <Tag color="blue">{record.prefix}</Tag> : null}
      {record.include_year !== false ? <Tag color="cyan">YYYY</Tag> : null}
      {record.include_month ? <Tag color="geekblue">MM</Tag> : null}
      <Tag color="green">{'#'.repeat(pad)}</Tag>
      {record.separator ? <Text type="secondary">{record.separator}</Text> : null}
    </Space>
  );
}

export const DocumentSequencesPage: React.FC = () => {
  const { t } = useTranslation('finance');
  const { t: tc } = useTranslation('common');
  const companyId = useAuthStore((s) => s.user?.company_id);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSequenceId, setEditingSequenceId] = useState<number | undefined>();
  const [previewNumber, setPreviewNumber] = useState<string>('');
  const [form] = Form.useForm();
  const watchedType = Form.useWatch('document_type', form);
  const [searchText, setSearchText] = useState('');
  const { data: sequences, isLoading } = useDocumentSequences(companyId);
  const { data: sequenceTypeOptions = [] } = useQuery({
    queryKey: ['document-sequence-types'],
    queryFn: () => settingsService.getDocumentSequenceTypes(),
  });
  const saveMutation = useSaveDocumentSequence();

  const typeLabelByValue = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of sequenceTypeOptions) {
      map.set(o.value, o.label);
    }
    return map;
  }, [sequenceTypeOptions]);

  const filteredSequences = useMemo(() => {
    const list = sequences ?? [];
    const q = searchText.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) => {
      const type = String(row.document_type || '');
      const label = typeLabelByValue.get(type) || '';
      const reset = resetPolicyFromRecord(row);
      const haystack = [
        type,
        type.replace(/_/g, ' '),
        label,
        row.prefix,
        row.separator,
        row.example_output,
        String(row.current_number ?? ''),
        reset,
        reset === 'never' ? 'continuous' : '',
        row.is_active === false ? 'inactive' : 'active',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [sequences, searchText, typeLabelByValue]);

  useEffect(() => {
    if (!isModalVisible || !companyId || !watchedType) {
      setPreviewNumber('');
      return;
    }
    let cancelled = false;
    settingsService
      .previewDocumentNumber(companyId, String(watchedType))
      .then((n) => {
        if (!cancelled) setPreviewNumber(n);
      })
      .catch(() => {
        if (!cancelled) setPreviewNumber('');
      });
    return () => {
      cancelled = true;
    };
  }, [isModalVisible, companyId, watchedType, editingSequenceId]);

  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({
      separator: '-',
      current_number: 0,
      padding_length: 4,
      include_year: true,
      include_month: false,
      reset_policy: 'never',
      is_active: true,
    });
    setEditingSequenceId(undefined);
    setIsModalVisible(true);
  };

  const handleEdit = (record: DocumentSequence) => {
    form.setFieldsValue({
      ...record,
      reset_policy: resetPolicyFromRecord(record),
      include_year: record.include_year !== false,
      include_month: !!record.include_month,
      is_active: record.is_active !== false,
      padding_length: record.padding_length ?? 4,
    });
    setEditingSequenceId(record.id);
    setIsModalVisible(true);
  };

  const handleSave = async (values: Record<string, unknown>) => {
    if (!companyId) {
      message.error(t('noCompanyAssociated'));
      return;
    }
    const policy = (values.reset_policy as ResetPolicy) || 'never';
    const flags = resetFlagsFromPolicy(policy);
    try {
      await saveMutation.mutateAsync({
        companyId,
        body: {
          id: values.id != null ? Number(values.id) : undefined,
          document_type: String(values.document_type ?? ''),
          prefix: values.prefix != null ? String(values.prefix) : undefined,
          separator: values.separator != null ? String(values.separator) : undefined,
          current_number:
            values.current_number != null && values.current_number !== ''
              ? Number(values.current_number)
              : undefined,
          padding_length:
            values.padding_length != null && values.padding_length !== ''
              ? Number(values.padding_length)
              : undefined,
          include_year: values.include_year !== false,
          include_month: values.include_month === true,
          reset_yearly: flags.reset_yearly,
          reset_monthly: flags.reset_monthly,
          is_active: values.is_active !== false,
        },
      });
      message.success(t('documentSequenceSaved'));
      setIsModalVisible(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || t('errSaveSequence'));
    }
  };

  const resetLabel = (p: ResetPolicy) => {
    if (p === 'yearly') return t('resetYearly');
    if (p === 'monthly') return t('resetMonthly');
    return t('resetNever');
  };

  const columns: ColumnsType<DocumentSequence> = [
    {
      title: t('documentType'),
      dataIndex: 'document_type',
      key: 'document_type',
      render: (text) => <Text strong>{String(text || '').replace(/_/g, ' ')}</Text>,
    },
    {
      title: t('format'),
      key: 'format',
      render: (_, record) => formatTags(record),
    },
    {
      title: t('resetPolicy'),
      key: 'reset_policy',
      render: (_, record) => {
        const p = resetPolicyFromRecord(record);
        const color = p === 'monthly' ? 'orange' : p === 'yearly' ? 'gold' : 'default';
        return <Tag color={color}>{resetLabel(p)}</Tag>;
      },
    },
    {
      title: t('currentNumber'),
      dataIndex: 'current_number',
      key: 'current_number',
      align: 'center',
    },
    {
      title: t('exampleOutput'),
      dataIndex: 'example_output',
      key: 'example_output',
      render: (text) => (text ? <Tag color="purple">{text}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: t('status'),
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean | undefined) => (
        <Tag color={active === false ? 'default' : 'success'}>
          {active === false ? t('inactive') : t('active')}
        </Tag>
      ),
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
          {tc('edit')}
        </Button>
      ),
    },
  ];

  return (
    <div>
      {!companyId ? (
        <Card style={{ marginBottom: 16 }}>
          <Text type="warning">{t('noCompanyLinkedSequences')}</Text>
        </Card>
      ) : null}
      <Card
        title={t('documentSequences')}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            {t('addSequence')}
          </Button>
        }
      >
        <Row style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={10} lg={8}>
            <Search
              placeholder={t('searchSequences')}
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
        </Row>
        <Table
          columns={columns as ColumnsType<DocumentSequence>}
          dataSource={filteredSequences}
          loading={isLoading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => t('sequenceCount', { count: total }),
          }}
        />
      </Card>

      <Modal
        title={t('configureDocumentSequence')}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={saveMutation.isPending}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="id" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item label={t('documentType')} name="document_type" rules={[{ required: true }]}>
            <Select
              placeholder={t('selectDocumentType')}
              disabled={editingSequenceId != null}
              showSearch
              optionFilterProp="label"
              onChange={(v) => {
                const o = sequenceTypeOptions.find((x) => x.value === v);
                if (o && editingSequenceId == null) {
                  form.setFieldsValue({ prefix: o.defaultPrefix });
                }
              }}
            >
              {sequenceTypeOptions.map((o) => (
                <Option key={o.value} value={o.value} label={`${o.label} ${o.value}`}>
                  {o.label} <Text type="secondary">({o.value})</Text>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('prefix')} name="prefix">
                <Input placeholder={t('prefixPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('separator')} name="separator" initialValue="-">
                <Input placeholder={t('separatorPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('currentNumber')} name="current_number" initialValue={0}>
                <Input type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('paddingLength')} name="padding_length" initialValue={4}>
                <Input type="number" min={1} max={12} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('includeYear')} name="include_year" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('includeMonth')} name="include_month" valuePropName="checked" initialValue={false}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label={t('resetPolicy')} name="reset_policy" initialValue="never">
                <Select>
                  <Option value="never">{t('resetNeverContinuous')}</Option>
                  <Option value="yearly">{t('resetYearly')}</Option>
                  <Option value="monthly">{t('resetMonthly')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('isActive')} name="is_active" valuePropName="checked" initialValue={true}>
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {previewNumber ? (
            <Text type="secondary">
              {t('nextNumberPreview')} <Tag color="purple">{previewNumber}</Tag>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {t('savedFormatFlagsHint')}
              </Text>
            </Text>
          ) : null}
        </Form>
      </Modal>
    </div>
  );
};
